import { useId } from 'react';

import type { ErrorStateKind } from './error-state-config';

/** Inline SVG inherits all Appearance tokens; an external <img> SVG cannot. */
export function ErrorIllustration({ kind }: { kind: ErrorStateKind }) {
  const uid = useId().replaceAll(':', '');
  const glass = 'url(#' + uid + '-glass)';
  const floor = 'url(#' + uid + '-floor)';
  const portal = 'url(#' + uid + '-portal)';
  const beam = 'url(#' + uid + '-beam)';
  return (
    <svg aria-hidden="true" className="slider-error-illustration"
      focusable="false" viewBox="0 0 620 360" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={uid + '-glass'} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--error-art-accent)" stopOpacity=".18" />
          <stop offset=".56" stopColor="var(--error-art-foreground)" stopOpacity=".035" />
          <stop offset="1" stopColor="var(--error-art-accent)" stopOpacity=".09" />
        </linearGradient>
        <radialGradient id={uid + '-floor'}>
          <stop offset="0" stopColor="var(--error-art-accent)" stopOpacity=".3" />
          <stop offset="1" stopColor="var(--error-art-accent)" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={uid + '-portal'} x1="0" y1="1" x2=".65" y2="0">
          <stop offset="0" stopColor="var(--error-art-accent)" stopOpacity=".06" />
          <stop offset=".65" stopColor="var(--error-art-accent)" stopOpacity=".78" />
          <stop offset="1" stopColor="var(--error-art-foreground)" stopOpacity=".95" />
        </linearGradient>
        <linearGradient id={uid + '-beam'} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--error-art-accent)" stopOpacity=".34" />
          <stop offset="1" stopColor="var(--error-art-accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <ellipse cx="304" cy="294" rx="209" ry="58" fill={floor} />
      <g fill="none" stroke="var(--error-art-muted)" strokeWidth=".85"
        opacity=".32" strokeLinejoin="round">
        <path d="M44 302H560M84 292H529M116 282H492" />
        <path d="M95 328L235 272M185 332L269 271M315 336L305 271M420 333L343 273M534 325L382 270" />
      </g>
      {kind === '404' && (
        <>
          <g fill={glass} stroke="var(--error-art-stroke)" strokeWidth="1.4" strokeLinejoin="round">
            <path d="M113 186L184 161V271L113 288Z" opacity=".43" />
            <path d="M172 112L296 57L366 109V277L260 301L172 261Z" />
            <path d="M172 112L260 149L366 109M260 149V301" fill="none" opacity=".52" />
            <path d="M366 108L473 78V279L366 301Z" />
          </g>
          <path d="M235 274V189C235 151 264 124 300 123C336 122 359 149 359 186V275Z"
            fill={portal} opacity=".82" />
          <path d="M247 277V189C247 158 269 136 300 135C329 134 347 154 347 189V277"
            fill="none" stroke="var(--error-art-foreground)" strokeWidth="1.6" opacity=".78" />
          <path d="M267 273V191Q267 157 301 157Q329 157 329 194V272Z"
            fill="var(--error-art-accent)" opacity=".15" />
          <path d="M264 274L181 318L388 318L346 277Z" fill={beam} opacity=".65" />
          <path d="M260 298L359 276" fill="none" stroke="var(--error-art-foreground)"
            strokeOpacity=".35" />
          <text x="417" y="161" fill="var(--error-art-foreground)" fontFamily="var(--error-art-font)"
            fontSize="43" textAnchor="middle" opacity=".94">404</text>
        </>
      )}
      {(kind === '403' || kind === '401') && (
        <>
          <g fill={glass} stroke="var(--error-art-stroke)" strokeWidth="1.4" strokeLinejoin="round">
            <path d="M151 174L210 153V278L151 293Z" opacity=".4" />
            <path d="M183 116L324 68L450 114V272L313 318L183 266Z" />
            <path d="M183 116L313 164L450 114M313 164V318" fill="none" opacity=".64" />
            <path d="M255 161L367 129V276L255 300Z" />
          </g>
          <path d="M257 306L313 321L450 272" fill="none" stroke="var(--error-art-accent)"
            strokeOpacity=".58" />
          <g transform="translate(273 175) skewY(-9)" fill="none" stroke="var(--error-art-foreground)"
            strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="39" width="62" height="56" rx="7"
              fill="var(--error-art-accent)" fillOpacity=".14" strokeWidth="2.2" />
            <path d="M21 39V27C21 3 59 3 59 27V39" strokeWidth="3.2" />
            <circle cx="40" cy="64" r="4.2" fill="var(--error-art-foreground)" stroke="none" />
            <path d="M40 69V78" strokeWidth="3" />
          </g>
          <text x="390" y="172" fill="var(--error-art-foreground)" fontFamily="var(--error-art-font)"
            fontSize="43" textAnchor="middle" opacity=".94">{kind}</text>
        </>
      )}
      {kind === '500' && (
        <>
          <g fill={glass} stroke="var(--error-art-stroke)" strokeWidth="1.4" strokeLinejoin="round">
            <path d="M176 118L325 105L368 129V173L216 192L176 169Z" />
            <path d="M176 184L325 170L368 193V237L216 258L176 231Z" />
            <path d="M176 251L325 238L368 259V296L217 315L176 289Z" />
            <path d="M368 110L461 78V280L368 306Z" />
          </g>
          <path d="M216 192V149L368 129M216 258V213L368 193M217 315V278L368 259"
            stroke="var(--error-art-stroke)" fill="none" opacity=".63" />
          <g fill="var(--error-art-foreground)" opacity=".84">
            <circle cx="194" cy="151" r="3" /><circle cx="205" cy="150" r="3" />
            <circle cx="216" cy="149" r="3" /><circle cx="194" cy="217" r="3" />
            <circle cx="205" cy="216" r="3" /><circle cx="216" cy="215" r="3" />
            <circle cx="194" cy="281" r="3" /><circle cx="205" cy="280" r="3" />
            <circle cx="216" cy="279" r="3" />
          </g>
          <path d="M312 282L369 186Q373 178 378 186L436 282Q440 290 429 292L317 292Q306 292 312 282Z"
            fill="var(--error-art-accent)" fillOpacity=".24"
            stroke="var(--error-art-foreground)" strokeWidth="2.2" />
          <path d="M374 220V253" stroke="var(--error-art-foreground)"
            strokeWidth="6" strokeLinecap="round" />
          <circle cx="374" cy="268" r="4.2" fill="var(--error-art-foreground)" />
          <text x="412" y="164" fill="var(--error-art-foreground)" fontFamily="var(--error-art-font)"
            fontSize="43" textAnchor="middle" opacity=".94">500</text>
        </>
      )}
      {kind === 'unexpected' && (
        <>
          <g fill={glass} stroke="var(--error-art-stroke)" strokeWidth="1.4" strokeLinejoin="round">
            <path d="M137 170L216 140V272L137 290Z" opacity=".42" />
            <path d="M354 153L490 113V282L354 312Z" opacity=".44" />
            <path d="M184 92L405 62L431 260L207 295Z" />
          </g>
          <path d="M185 132L409 102" stroke="var(--error-art-stroke)" strokeWidth="1.4" />
          <g fill="var(--error-art-accent)" opacity=".95">
            <circle cx="207" cy="109" r="3.8" /><circle cx="219" cy="107" r="3.8" />
            <circle cx="231" cy="106" r="3.8" />
          </g>
          <ellipse cx="311" cy="192" rx="48" ry="48" transform="rotate(-8 311 192)"
            fill="var(--error-art-accent)" fillOpacity=".1"
            stroke="var(--error-art-foreground)" strokeWidth="2.2" />
          <path d="M311 168V201" stroke="var(--error-art-foreground)"
            strokeWidth="6" strokeLinecap="round" />
          <circle cx="311" cy="215" r="4.1" fill="var(--error-art-foreground)" />
          <path d="M207 295L431 260" stroke="var(--error-art-accent)" strokeOpacity=".62" />
        </>
      )}
    </svg>
  );
}
